import axios, { type InternalAxiosRequestConfig } from 'axios'
import type { IApiError } from '@/shared/interfaces/api-error.interface'
import { EErrorCode } from '@/shared/enum/error-code.enum'
import { EResponseStatus } from '@/shared/enum/response-status.enum'
import { useAppStore } from '@/stores/app'
import { useTokenStore } from '@/stores/token'
import getMceUrlByCountry from '@/shared/utils/get-mce-url-by-country'
import getCurrentCountry from '@/shared/utils/get-current-country'

const api = axios.create({
  baseURL: getMceUrlByCountry(getCurrentCountry()),
  timeout: 20000,
  headers: {
    'content-type': 'application/json'
  }
})

const setTokenHeader = (config: InternalAxiosRequestConfig<any>, token: string) => {
  if (!token) return config

  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `${token}`)
    config.headers.set('X-Authorization', `${token}`)
  } else if (config.headers) {
    config.headers['Authorization'] = `Bearer ${token}`
    config.headers['X-Authorization'] = `Bearer ${token}`
  }

  return config
}

api.interceptors.request.use(
  (config) => {
    const appStore = useAppStore()
    const tokenStore = useTokenStore()

    appStore.setLoading(true)

    const token = tokenStore.token ? `Bearer ${tokenStore.token}` : ''

    return setTokenHeader(config, token)
  },
  (error) => {
    const appStore = useAppStore()
    appStore.setLoading(false)
    return Promise.reject(error)
  }
)

api.interceptors.response.use(
  (config) => {
    const appStore = useAppStore()
    appStore.setLoading(false)
    return config
  },
  (error) => {
    const appStore = useAppStore()
    const normalizeError: IApiError = {
      message: error.message,
      status: error.response?.status || EResponseStatus.INTERNAL_ERROR,
      code: error.code || EErrorCode.DEFAULT_ERROR
    }
    appStore.setLoading(false)
    return Promise.reject(normalizeError)
  }
)

export default api
